import { useCallback } from 'react';

import { ClaimContext, OnChangeClaimContext } from '../types';
import { useFileImport } from './useFileImport';
import { useClaimsShopSettings } from '../../ClaimsShopSettingsContext';
import container from '../../../../container';
import { isDeclarationOfRecipientStepRequired } from '../claimsHelpers';

export interface UseImportDocumentsProps {
  shopId: number;
  onChange: OnChangeClaimContext;
  extractInvoiceData: (fileId: number) => void;
}

export function useImportDocuments(props: UseImportDocumentsProps) {
  const { shopId, onChange, extractInvoiceData } = props;
  const { isInvoiceImportEnabled, isDORSigningLinkEnabled } = useClaimsShopSettings();

  const [importInvoice, importInvoiceState] = useFileImport({
    fileKey: 'invoiceDocuments',
    onImportFile: ({ trackingCode, shopOrderId }: { trackingCode: string; shopOrderId: string | null | undefined }) =>
      container.touchpointsApi.postClaimsImportInvoice(shopId, { trackingCode, shopOrderId }),
    shopId,
    onChange,
    onSuccess: (file) => extractInvoiceData(file.id),
  });

  const [importDOR, importDORState] = useFileImport({
    fileKey: 'declarationOfRecipient',
    onImportFile: (shipmentId: number) => container.touchpointsApi.postClaimsImportDOR(shopId, { shipmentId }),
    shopId,
    onChange,
  });

  const importInvoiceIfRequired = useCallback(
    (context: ClaimContext) => {
      const { trackingCode, invoiceDocuments, shopOrderId } = context;

      if (isInvoiceImportEnabled && trackingCode && invoiceDocuments.length === 0) {
        importInvoice({ trackingCode, shopOrderId });
      }
    },
    [importInvoice, isInvoiceImportEnabled]
  );

  const importDORIfRequired = useCallback(
    (context: ClaimContext) => {
      const { shipmentId, declarationOfRecipient } = context;

      if (
        isDORSigningLinkEnabled &&
        isDeclarationOfRecipientStepRequired(context) &&
        shipmentId &&
        declarationOfRecipient.length === 0
      ) {
        importDOR(shipmentId);
      }
    },
    [importDOR, isDORSigningLinkEnabled]
  );

  const importDocuments = useCallback(
    (claim: ClaimContext) => {
      importInvoiceIfRequired(claim);
      importDORIfRequired(claim);
    },
    [importInvoiceIfRequired, importDORIfRequired]
  );

  return { importDocuments, importDORState, importInvoiceState };
}
